import axios from 'axios';

/**
 * 로컬스토리지 키 상수
 */
const SERVICE_USER_ID_KEY = 'serviceUserId';

/**
 * 로컬스토리지에서 serviceUserId 조회
 */
const getServiceUserId = (): string | null => {
  try {
    return localStorage.getItem(SERVICE_USER_ID_KEY);
  } catch (error) {
    console.warn('[API Config] Failed to get serviceUserId from localStorage:', error);
    return null;
  }
};

/**
 * 로컬스토리지에 serviceUserId 저장
 */
const setServiceUserId = (serviceUserId: string): void => {
  try {
    localStorage.setItem(SERVICE_USER_ID_KEY, serviceUserId);
  } catch (error) {
    console.error('[API Config] Failed to save serviceUserId to localStorage:', error);
  }
};

// 환경 변수에서 API 기본 URL 가져오기
const getApiBaseUrl = () => {
  // 1. 환경 변수에서 직접 API URL 설정 가져오기
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // 2. 개발 서버 환경 감지 (delivereee.brainbackdoor.com)
  const isDevServer = typeof window !== 'undefined' && window.location.hostname.includes('brainbackdoor.com');
  
  // 3. 현재 URL 기반으로 프로덕션 여부 체크 (GitHub Pages 환경 감지)
  const isGitHubPages = typeof window !== 'undefined' && window.location.hostname.includes('github.io');
  
  // 4. NODE_ENV 환경 변수 확인
  const isProdEnv = process.env.NODE_ENV === 'production';
  
  // 개발 서버에서 실행 중이면 localhost:8080 사용
  if (isDevServer) {
    return 'http://localhost:8080';
  }
  
  // 프로덕션 또는 GitHub Pages에서 실행 중이면 운영 API URL 사용
  if (isProdEnv || isGitHubPages) {
    return 'https://api.brainbackdoor.com';
  }
  
  // 로컬 개발 환경에서는 로컬 URL 사용
  return 'http://localhost:8084';
};

const API_BASE_URL = getApiBaseUrl();
const isDevelopment = process.env.NODE_ENV === 'development';

// axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 개발 환경에서만 서버 상태 확인
if (isDevelopment) {
  checkServerStatus();
}

/**
 * API 서버 상태 확인 (개발 환경용)
 * 가벼운 헬스체크 엔드포인트 사용
 */
async function checkServerStatus() {
  try {
    const existingServiceUserId = getServiceUserId();
    
    // API 요청 URL 구성
    let apiUrl = `/api/v1/live-on?lang=en`;
    if (existingServiceUserId) {
      apiUrl += `&serviceUserId=${existingServiceUserId}`;
    }
    
    // 1순위: 헬스체크 전용 엔드포인트 시도
    const response = await axios.get(`${API_BASE_URL}${apiUrl}`, { timeout: 3000 });
    
    // 응답에서 serviceUserId 처리
    if (response.data?.serviceUserId && response.data.serviceUserId !== existingServiceUserId) {
      setServiceUserId(response.data.serviceUserId);
    }
    
  } catch (healthError: any) {
    try {
      const existingServiceUserId = getServiceUserId();
      
      // API 요청 URL 구성
      let apiUrl = `/api/v1/live-on?lang=en`;
      if (existingServiceUserId) {
        apiUrl += `&serviceUserId=${existingServiceUserId}`;
      }
      
      // 2순위: 더 가벼운 엔드포인트 시도 (헬스체크가 없는 경우)
      const response = await axios.get(`${API_BASE_URL}${apiUrl}`, { timeout: 3000 });
      
      // 응답에서 serviceUserId 처리
      if (response.data?.serviceUserId && response.data.serviceUserId !== existingServiceUserId) {
        setServiceUserId(response.data.serviceUserId);
      }
      
    } catch (liveOnError: any) {
      // 403 에러인 경우에도 serviceUserId 처리
      if (liveOnError.response?.status === 403 && liveOnError.response?.data?.serviceUserId) {
        const existingServiceUserId = getServiceUserId();
        if (liveOnError.response.data.serviceUserId !== existingServiceUserId) {
          setServiceUserId(liveOnError.response.data.serviceUserId);
        }
      }
      console.warn('⚠️ API 서버 상태 확인 실패 - 헬스체크 엔드포인트 없음');
    }
  }
}

// 요청 인터셉터 - 요청 전에 실행됨
apiClient.interceptors.request.use(
  (config) => {
    // 개발/배포 환경 모두에서 API 요청 허용
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 응답 후에 실행됨
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 에러 처리 로직
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

export default apiClient;
